import { useState } from 'react'
import { PRODUCT_NAME } from '../brand.js'

/**
 * Password Reset — REQUEST and CONFIRM screens.
 *
 * ┌─ BOTH ARE DISABLED. THERE IS NO BACKEND. ────────────────────────────────┐
 * │ There are no /auth/password-reset endpoints, no SMTP or mail transport   │
 * │ anywhere in the project, and no reset-token table in the schema. Nothing │
 * │ here can work yet.                                                       │
 * │                                                                          │
 * │ They are built so the layout and both themes are verifiable now, and so  │
 * │ enabling them later is deleting a flag rather than writing a screen. The │
 * │ submit is disabled and no request is ever sent, so nobody clicking       │
 * │ through hits a confusing 404.                                            │
 * │                                                                          │
 * │ THE NOTICE SAYS WHAT TO DO INSTEAD. An administrator can reset a         │
 * │ password directly today, so the copy points there. A notice that only    │
 * │ said "not available" would leave someone locked out with nowhere to go,  │
 * │ which is the actual problem they came to solve.                          │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Wiring up is tracked in docs/TASKS.md §6 with the SMTP dependency.
 */

/** Flip to false once the endpoints exist. Nothing else needs to change. */
const PASSWORD_RESET_AVAILABLE = false

function UnavailableNotice(): React.JSX.Element {
  return (
    <div className="auth__message auth__message--warning">
      <p className="auth__message-title">Password Reset By Email Is Not Available Yet</p>
      <p>
        This needs email sending to be set up on the server, which is not configured yet.
      </p>
      <p>
        Ask an administrator to reset your password for you. They can do this directly and
        it takes a moment.
      </p>
    </div>
  )
}

type RequestProps = { onBack: () => void }

export function PasswordResetRequest({ onBack }: RequestProps): React.JSX.Element {
  const [email, setEmail] = useState('')

  return (
    <div className="auth">
      <div className="auth__card">
        <p className="auth__brand">{PRODUCT_NAME}</p>
        <h1 className="auth__title">Reset Your Password</h1>
        <p className="auth__subtitle">
          Enter your email address and we will send you a link to set a new password.
        </p>

        {!PASSWORD_RESET_AVAILABLE && <UnavailableNotice />}

        {/* onSubmit is deliberately a no-op while disabled: there is nothing to call. */}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="auth__field">
            <label className="auth__label" htmlFor="reset-email">
              Email Address
            </label>
            <input
              id="reset-email"
              className="auth__input"
              type="email"
              autoComplete="username"
              disabled={!PASSWORD_RESET_AVAILABLE}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <button type="submit" className="auth__button" disabled={!PASSWORD_RESET_AVAILABLE}>
            Send Reset Link
          </button>
        </form>

        <div className="auth__links">
          <button type="button" className="auth__link" onClick={onBack}>
            Back To Sign In
          </button>
        </div>
      </div>
    </div>
  )
}

type ConfirmProps = { onBack: () => void }

export function PasswordResetConfirm({ onBack }: ConfirmProps): React.JSX.Element {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const tooShort = password.length > 0 && password.length < 12
  const mismatch = confirm.length > 0 && password !== confirm

  return (
    <div className="auth">
      <div className="auth__card">
        <p className="auth__brand">{PRODUCT_NAME}</p>
        <h1 className="auth__title">Set A New Password</h1>
        <p className="auth__subtitle">Choose a new password for your account.</p>

        {!PASSWORD_RESET_AVAILABLE && <UnavailableNotice />}

        {/*
          Stated up front, not after submitting. Signing out everywhere is the
          correct behaviour for a password reset — if the reason for resetting
          is a compromised password, leaving other sessions alive defeats the
          point — but it is a surprise if someone finds out by being logged out
          on their phone.
        */}
        <div className="auth__message auth__message--info">
          <p>
            Setting a new password signs you out on every other device. You will need to
            sign in again there.
          </p>
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="auth__field">
            <label className="auth__label" htmlFor="new-password">
              New Password
            </label>
            <input
              id="new-password"
              className="auth__input"
              type="password"
              autoComplete="new-password"
              minLength={12}
              disabled={!PASSWORD_RESET_AVAILABLE}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {tooShort && (
              <span className="auth__meta">Must Be At Least 12 Characters</span>
            )}
          </div>

          <div className="auth__field">
            <label className="auth__label" htmlFor="confirm-password">
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              className="auth__input"
              type="password"
              autoComplete="new-password"
              disabled={!PASSWORD_RESET_AVAILABLE}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <span className="auth__meta">Passwords Do Not Match</span>}
          </div>

          <button
            type="submit"
            className="auth__button"
            disabled={!PASSWORD_RESET_AVAILABLE || tooShort || mismatch || password.length === 0}
          >
            Set New Password
          </button>
        </form>

        <div className="auth__links">
          <button type="button" className="auth__link" onClick={onBack}>
            Back To Sign In
          </button>
        </div>
      </div>
    </div>
  )
}
